import React, { useState, useEffect } from 'react';
import { Modal, Spin, Select, Form, Input, DatePicker, Button, message } from 'antd';
import { SaveFilled } from '@ant-design/icons';
import { getGapUsers } from "../../../../../../API/users";
import { createTask, getIssueByCode } from "../../../../../../API/gaps";
import { getTaskStatus } from "../../../../../../API/tasks";
import dayjs from 'dayjs';

const { TextArea } = Input;

const AssignTasks = ({
  visible,
  onCancel,
  selectedIssue,
  issueAreas,
  loading,
  api,
  onTaskAdded
}) => {
  const [form] = Form.useForm();
  const [newTask, setNewTask] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [taskStatuses, setTaskStatuses] = useState([]);
  const selectedStatusId = Form.useWatch('status', form);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsSubmitting(true);
        if (visible && selectedIssue?.code) {
          // ✅ Resetear formulario antes de cargar datos
          form.resetFields();
          setNewTask('');
          
          // Cargar estados de tareas
          const statuses = await getTaskStatus();
          setTaskStatuses(statuses);
          
          // Establecer el estado inicial (Pendiente - index 1)
          const pendingStatus = statuses.find(s => s.index === 1);
          if (pendingStatus) {
            form.setFieldsValue({ status: pendingStatus.id });
          }
          
          await getIssueByCode(selectedIssue.code);  
        }
      } catch (error) {
        api.error({ message: 'Error al cargar los datos de la issue' });
        console.error(error);
      } finally {
        setIsSubmitting(false);
      }
    };

    if (visible) {
      fetchData();
    } else {
      // ✅ Limpiar al cerrar
      form.resetFields();
      setNewTask('');
    }
  }, [visible, selectedIssue]);

  useEffect(() => {
    const finishedStatus = taskStatuses.find(s => s.index === 3);
    
    if (selectedStatusId === finishedStatus?.id) {
      form.setFieldsValue({ 
        date_finished: dayjs()
      });
      
      // ✅ NUEVO: Auto-llenar fecha comprometida si está vacía
      const currentDateCommitted = form.getFieldValue('date_committed');
      if (!currentDateCommitted) {
        form.setFieldsValue({
          date_committed: dayjs()
        });
      }
    } else {
      form.setFieldsValue({ date_finished: null });
    }
  }, [selectedStatusId, form, taskStatuses]);

  const handleAddTask = async () => {
    if (!newTask.trim()) return;
    
    try {
      setIsSubmitting(true);
      const values = form.getFieldsValue();
      
      const taskData = {
        issue_code: selectedIssue.code,
        description: values.description,
        status_id: values.status,  // ✅ Envía el ID al backend
        name: values.name,
        date_committed: values.date_committed?.toISOString(),
        date_finished: values.date_finished?.toISOString(),
        responsible_area_id: values.area
      };

      const createdTask = await createTask(taskData);
      onTaskAdded(createdTask);
      
      setNewTask('');
      api.success({ message: 'Tarea agregada correctamente' });
      setTimeout(() => {
        window.location.reload();
      }, 600);
    } catch (error) {
      api.error({ message: 'Error al agregar la tarea' });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = () => {
    form.validateFields()
      .then(() => {
        if (newTask.trim()) {
          handleAddTask();
        }
      })
      .catch(() => {});
  };

  return (
    <Modal
      className="dependency-modal"
      title={<>Crear tarea para {selectedIssue?.type}</>}
      open={visible}
      onCancel={onCancel}
      onOk={handleSave}
      confirmLoading={isSubmitting}
      cancelButtonProps={{ className: 'cancel-button' }}
      okButtonProps={{
        className: "save-type-button",
        icon: <SaveFilled />,
        disabled: !newTask.trim() || isSubmitting
      }}
      cancelText="Cancelar"
      okText="Guardar"
    >
      <Spin className="custom-spin" spinning={loading || isSubmitting}>
        <Form form={form} layout="vertical">
          <div style={{ marginBottom: 24 }}>
            <Form.Item
              label="Nombre"
              name="name"
              rules={[{ required: true, message: 'El nombre es requerido' }]}
            >
              <Input.TextArea
                maxLength={50} 
                showCount
                style={{ resize: 'none', width: '100%' }} 
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                placeholder="Nombre de la tarea"
                disabled={isSubmitting}
              />
            </Form.Item>
            <Form.Item
              label="Descripción"
              name="description"
              rules={[{ required: true, message: 'La descripción es requerida' }]}
            >
              <Input.TextArea 
                maxLength={300} 
                showCount 
                style={{ resize: 'none', width: '100%', height: '90px' }} 
              />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label="Estado"
                name="status"
                rules={[{ required: true, message: 'El estado es requerido' }]}
              >
                <Select placeholder="Seleccione un estado">
                  {taskStatuses.map((status) => (
                    <Select.Option key={status.index} value={status.id}>
                      {status.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item 
                label="Responsable"
                name="area"
                rules={[{ required: true, message: 'Este campo es requerido' }]}
              >
                <Select allowClear placeholder="Seleccione un área responsable">
                  {(issueAreas || []).map((area) => (
                    <Select.Option key={area.id} value={area.id}>
                      {area.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Tipo">
                <Input value={selectedIssue?.type || 'N/A'} disabled />
              </Form.Item>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item 
              label="Fecha Comprometida" 
              name="date_committed"
              required={(() => {
                const initiatedStatus = taskStatuses.find(s => s.index === 2);
                return selectedStatusId === initiatedStatus?.id;
              })()}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const statusId = getFieldValue('status');
                    const initiatedStatus = taskStatuses.find(s => s.index === 2);
                    const pendingStatus = taskStatuses.find(s => s.index === 1);
                    
                    // Estado "Pendiente" (index 1) => NO es obligatorio
                    if (statusId === pendingStatus?.id) {
                      return Promise.resolve();
                    }

                    // Estado "Iniciado" (index 2) => Obligatorio
                    if (statusId === initiatedStatus?.id && !value) {
                      return Promise.reject(new Error('La fecha comprometida es obligatoria'));
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD/MM/YYYY"
                inputReadOnly
                disabled={(() => {
                  const finishedStatus = taskStatuses.find(s => s.index === 3);
                  
                  return selectedStatusId === finishedStatus?.id;
                })()}
                disabledDate={(current) => {
                  return current && current < dayjs().startOf('day');
                }}
              />
            </Form.Item>
            
            <Form.Item 
              label="Fecha Finalización" 
              name="date_finished"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const statusId = getFieldValue('status');
                    const finishedStatus = taskStatuses.find(s => s.index === 3);
                    
                    // ✅ Comparar con el ID del estado "Finalizado"
                    if (statusId === finishedStatus?.id && !value) {
                      return Promise.reject(new Error('Este campo es requerido'));
                    }
                    
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD/MM/YYYY"
                disabled={true}
                inputReadOnly
                /* disabled={(() => {
                  const finishedStatus = taskStatuses.find(s => s.index === 3);
                  // Bloquear edición si el estado actual es Finalizado
                  return selectedStatusId === finishedStatus?.id;
                })()} */
                disabledDate={(current) => {
                  // Permitir solo HOY en adelante
                  return current && current < dayjs().startOf('day');
                }}
              />
            </Form.Item>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default AssignTasks;